import { useContext, useEffect, useRef, forwardRef, useImperativeHandle, useState } from "react";
import { GEOFENCE_NOTIFICATION_TYPE, GEOFENCE_TRIGGER_TYPE, NOTIF_TYPE_VALS, NOTIF_TYPES, OVERSPEEd_NOTIFICATION_TYPE, OVERSPEED_TRIGGER_TYPE, NOTIFS_DEFAULT_LIMIT, SEUDO_EVENT_NOTIFICATION_TYPE } from "../../../Library/constants";
import { StoreContext } from "../../../Library/contexts";
import { getLocalisedDate, getLocalisedTime, isSameDay } from "../../../Library/datetime";
import "./styles.css";
import { getAlerts } from "../../../APIs";
import { jsonToCSVConvertor } from "../../../Library/jsonUtils";

const getSlot = (ts) => {
  ts = ts * 1000;
  const TODAY_START = new Date().setHours(0, 0, 0, 0);
  const YESTERDAY_START = new Date(TODAY_START).setDate(new Date(TODAY_START).getDate() - 1);

  if (ts >= TODAY_START) return "Today";
  if (ts >= YESTERDAY_START) return "Yesterday";
  return getLocalisedDate(ts);
};

const NotifList = forwardRef(({ full, selectedVehs, selectedDateRange = {}, selectedAlerts, fetchingTlEventData = false, setFetchingTlEventData = () => {} }, ref) => {
  const { notifications, setNotifications, userVehicles, allGeofences, setAllGeofences, allOverspeedAlerts, setAllOverspeedAlerts } = useContext(StoreContext);
  let notifs = useRef([]);
  let vehMap = useRef({});
  let fenceMap = useRef({});
  let overspeedMap = useRef({});
  let uniqueVids = useRef(new Set());
  let vehicleOptions = useRef([]);
  let uniqueAlerts = useRef(new Set());
  let alertOptions = useRef([]);
  let originalData = useRef([]);

  let vidOldestWtsMap = useRef({});
  let vehicleWiseDataFetchedCountMap = useRef({});
  let vehHasMoreDataToFetchMap = useRef({});
  let scrollRestoreRef = useRef(null);
  const firstRunRef = useRef(true);
  const fetchingTlEventDataRef = useRef(fetchingTlEventData);

  useEffect(() => {
    fetchingTlEventDataRef.current = fetchingTlEventData;
  }, [fetchingTlEventData]);

  const downloadNotifications = () => {
    if (!notifications || !notifications.notifs.length) return;

    const csvData = notifications.notifs.reduce((rows, notif) => {
      const notificationType = NOTIF_TYPE_VALS[notif.t];
      if (!notificationType) return rows;
      if (notif.t === GEOFENCE_NOTIFICATION_TYPE && notif.meta && notif.meta.tid && !fenceMap.current[notif.meta.tid]) return rows;

      rows.push({
        "Vehicle Registration Number": notif.reg,
        "Alert Type": notificationType.nm,
        "Notification": getNotificationText(notif, notificationType).replace(/"/g, '""'),
        "Date": getLocalisedDate(notif.ts * 1000, true),
        "Time": getLocalisedTime(notif.ts * 1000, true, true),
      });
      return rows;
    }, []);

    jsonToCSVConvertor(csvData, "", true, `NOTIFICATIONS_${Date.now()}`, false);
  };

  useImperativeHandle(ref, () => ({
    callFetchTlEvents(dateRange) {
      resetAndFetch(dateRange);
    },
    downloadNotifications,
  }));

  useEffect(() => {
    // if (full) setNotifications((old) => ({ ...old, unreadCount: 0 }));
    if (setNotifications) global.EE.addEventListener(global.EE.EVENT_TYPES.GET_TL_EVENTS, handleNotifications);
    return () => {
      global.EE.removeEventListener(global.EE.EVENT_TYPES.GET_TL_EVENTS, handleNotifications);
    };
  }, [ full, setNotifications ]);

  useEffect(() => {
    if (!selectedVehs || !selectedAlerts) return;

    if (selectedVehs.length === vehicleOptions.current.length && selectedAlerts.length === alertOptions.current.length && notifications?.notifs.length !== originalData.current.length) {
      setNotifications({ unreadCount: 0, notifs: originalData.current });
    } else {
      let filteredNotifs = originalData.current.filter(notif => selectedVehs.includes(notif.vid) && selectedAlerts.includes(notif.t));
      setNotifications({ unreadCount: 0, notifs: filteredNotifs });
    }
  }, [ selectedVehs, selectedAlerts ]);

  const handleWtsFetchedCount = (notif) => {
    vidOldestWtsMap.current[notif.vid] = Math.min(vidOldestWtsMap.current[notif.vid] || Infinity, notif.wts );
    vehicleWiseDataFetchedCountMap.current[notif.vid] = (vehicleWiseDataFetchedCountMap.current[notif.vid] || 0) + 1;
  }

  const handleNotifications = (notif) => {
    if(notif.t === SEUDO_EVENT_NOTIFICATION_TYPE) return handleWtsFetchedCount(notif);
    if (notif.ts !== 0) {
      notif.reg = vehMap.current[notif.vid];
      notifs.current.push(notif);

      handleWtsFetchedCount(notif);

      if (!uniqueVids.current.has(notif.vid)) {
        uniqueVids.current.add(notif.vid)
        vehicleOptions.current.push({ value: notif.vid, label: notif.reg });
      }

      if (!uniqueAlerts.current.has(notif.t)) {
        uniqueAlerts.current.add(notif.t)
        let type = NOTIF_TYPE_VALS[notif.t];
        if (type) alertOptions.current.push({ value: notif.t, label: NOTIF_TYPE_VALS[notif.t].nm });
      }
    } else {
      setFetchingTlEventData(false);
      if (notifs.current.length === 0) return;

      Object.keys(vehicleWiseDataFetchedCountMap.current).forEach(vid => {
        if (vehicleWiseDataFetchedCountMap.current[vid] < NOTIFS_DEFAULT_LIMIT) vehHasMoreDataToFetchMap.current[vid] = false;
        else if (vehicleWiseDataFetchedCountMap.current[vid] === NOTIFS_DEFAULT_LIMIT) vehHasMoreDataToFetchMap.current[vid] = true;
      });

      Object.keys(vehHasMoreDataToFetchMap.current).forEach(vid => {
        if (vehicleWiseDataFetchedCountMap.current[vid] === undefined || vehicleWiseDataFetchedCountMap.current[vid] == null) vehHasMoreDataToFetchMap.current[vid] = false;
      });
      vehicleWiseDataFetchedCountMap.current = {};

      const newBatch = notifs.current;
      const allNotifications = [...originalData.current, ...newBatch].sort((a, b) => b.ts - a.ts);
      originalData.current = allNotifications;

      global.EE.emitEvent(global.EE.EVENT_TYPES.NOTIF_FILTER_OPTIONS, { vehs: vehicleOptions.current, alerts: alertOptions.current });

      setNotifications({ unreadCount: allNotifications.length, notifs: allNotifications });

      notifs.current = [];

      if (scrollRestoreRef.current != null) {
        const container = document.querySelector(".panel-notifications");
        if (container) {
          requestAnimationFrame(() => {
            container.scrollTop = scrollRestoreRef.current;
            scrollRestoreRef.current = null;
          });
        }
      }
    }
  }

  useEffect(() => {
    if ((!allGeofences || !allGeofences.length || !allOverspeedAlerts || !allOverspeedAlerts.length) && setAllGeofences) {
      getAlerts(null, (err, data) => {
        if (err) console.error("Error fetching geofence alerts", err);
        else {
          data.forEach(alert => {
            if (alert.typ === GEOFENCE_TRIGGER_TYPE) fenceMap.current[alert.idx] = alert.title;
            if (alert.typ === OVERSPEED_TRIGGER_TYPE) overspeedMap.current[alert.idx] = alert;
          });
          setAllGeofences(data.filter(alert => alert.typ === GEOFENCE_TRIGGER_TYPE));
          setAllOverspeedAlerts(data.filter(alert => alert.typ === OVERSPEED_TRIGGER_TYPE));
        }
      });
    } else {
      [...allGeofences, ...allOverspeedAlerts].forEach(alert => {
        if (alert.typ === GEOFENCE_TRIGGER_TYPE) fenceMap.current[alert.idx] = alert.title;
        if (alert.typ === OVERSPEED_TRIGGER_TYPE) overspeedMap.current[alert.idx] = alert;
      });
    }
  }, []);

  const fetchTlEvents = (payload = {}, selectedDate) => {
    const { scrollPos } = payload;
    scrollRestoreRef.current = scrollPos;
    if (userVehicles && userVehicles.length && selectedDate && selectedDate.from && selectedDate.to && !fetchingTlEventDataRef.current) {
      if (global.UWS && global.UWS.wsConnected) {
        let vehs = [];
        userVehicles.forEach(veh => {
          if (vehHasMoreDataToFetchMap.current[veh.idx]) {
            let vehReq = {
              vid: veh.idx,
              wts: vidOldestWtsMap.current[veh.idx] || Math.floor(selectedDate.to/1000),
              tsFr: Math.floor((new Date(selectedDate.from).setHours(0, 0, 0, 0))/1000),
            }
            vehs.push(vehReq);
          }
          vehMap.current[veh.idx] = veh.reg;
        });
        if (vehs.length) {
          setFetchingTlEventData(true);
          global.UWS.sendRequest({ action: global.EE.EVENT_TYPES.GET_TL_EVENTS, data: { veh: vehs, count: NOTIFS_DEFAULT_LIMIT } });
        } else {
          setFetchingTlEventData(false);
        }
      }
    }
  }

  const resetAndFetch = (dateRange) => {
    notifs.current = [];
    originalData.current = [];
    vidOldestWtsMap.current = {};
    vehicleWiseDataFetchedCountMap.current = {};

    userVehicles?.forEach(uVeh => vehHasMoreDataToFetchMap.current[uVeh.idx] = true);

    if (setNotifications && full) setNotifications({ unreadCount: 0, notifs: [] });
    fetchTlEvents({}, dateRange);
  };

  useEffect(() => {
    if (!userVehicles || !selectedDateRange || !selectedDateRange.from || !selectedDateRange.to || !global.UWS || !global.UWS.wsConnected) return;

    if (firstRunRef.current) {
      firstRunRef.current = false;
      resetAndFetch(selectedDateRange);
      return;
    }

    global.EE.addEventListener(global.EE.EVENT_TYPES.FETCH_TL_EVENTS, fetchTlEvents);
    return () => {
      global.EE.removeEventListener(global.EE.EVENT_TYPES.FETCH_TL_EVENTS, fetchTlEvents);
    };
  }, [userVehicles, selectedDateRange]);

  const renderSlot = ({ ts }) => {
    return (
      <div key={ts} className="notif-slot-cnt">
        <div className="notif-slot"> {getSlot(ts)} </div>
      </div>
    );
  };

  const getNotificationText = (notif, notifType) => {
    let notifText = "";
    if (notif.t === GEOFENCE_NOTIFICATION_TYPE) {
      notifText = notifType.text(notif.meta.igf, fenceMap.current[notif.meta.tid]);
    } else if (notif.t === OVERSPEEd_NOTIFICATION_TYPE) {
      let alert = overspeedMap.current[notif.meta.tid];
      let notifHint = "";
      if (alert && alert.gfId) {
        if (alert.out) notifHint = "outside";
        else notifHint = "inside";

        let fence = fenceMap.current[alert.gfId];
        if (fence) notifHint += ` "${fence}" Geofence`;
      }

      notifText = notifType.text(notif.meta.spd, notifHint);
    } else notifText = notifType.text;

    return notifText;
  }

  const renderNotif = (notification, isNew, i) => {
    const notificationType = NOTIF_TYPE_VALS[notification.t];
    if (!notificationType) return null;
    if (notification.t === GEOFENCE_NOTIFICATION_TYPE && notification.meta && notification.meta.tid && !fenceMap.current[notification.meta.tid]) return null;

    return (
      <div key={i} className="notif-item">
        <div className="notif-item-icon">
          {notification.t === GEOFENCE_NOTIFICATION_TYPE ? (notificationType.icon[notification.meta.igf]) : notificationType.icon}
        </div>

        <div className="notif-item-main">
          {/* <div className="notif-item-header">{notificationType.label}</div> */}
          <div className="notif-item-label">
            <span className="notif-veh">
              {notification.reg}
            </span>
            {getNotificationText(notification, notificationType)}
            {/* {notification.t === GEOFENCE_NOTIFICATION_TYPE ? notificationType.text(notification.meta.igf, fenceMap.current[notification.meta.tid]) : notificationType.text} */}
            {/* {JSON.stringify(notification)} */}
          </div>
        </div>

        <div className="notif-item-time">
          {getLocalisedTime(notification.ts * 1000, true, true)}
        </div>

        <div className="notif-item-end">
          <div className={"notif-item-badge " + (isNew ? "notif-item-badge-active" : "")} />
        </div>
      </div>
    );
  };

  const renderNotifications = () => {
    if (!notifications || !notifications.notifs.length) return (
      <div className="notif-slot"> No New Notifications </div>
    );

    const render = [];

    notifications.notifs.forEach((notif, i) => {
      if (!full && (i > 5)) return;

      const prevNotif = notifications.notifs[i - 1];
      if (!prevNotif || !isSameDay(notif.ts * 1000, prevNotif.ts * 1000)) render.push(renderSlot(notif));
      render.push(renderNotif(notif, i < notifications.unreadCount, i));
    });

    return render;
  };

  return (
    <>
      {renderNotifications()}
    </>
  );
});

export default NotifList;
